import React, { FC, useRef, useState, useEffect, ChangeEvent, useMemo } from 'react';
import { UploadIcon } from 'cokitchen-icons';
import axios from 'axios';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import { toast } from 'react-toastify';
import { STORAGE_KEYS } from 'utils/constants';
import { getFileSize, getTimeDuration } from 'utils/functions';
import { ImageDataType } from 'contexts/new-meal-xls.context/context';

type ImageUploaderProps = {
  files: Array<File>;
  setFiles: (item: Array<File>) => void;
  imageData: ImageDataType[];
  setImageData: (item: ImageDataType[]) => void;
  disabled?: boolean;
};

const XlsMealsImageUploader: FC<ImageUploaderProps> = ({
  files,
  setFiles,
  imageData,
  setImageData,
  disabled
}) => {
  const [percent, setPercent] = useState(0);
  const [timeLeft, setTimeLeft] = useState('');
  const [hasUploaded, setHasUploaded] = useState(false);
  const [allowDrop, setAllowDrop] = useState(true);
  const [uploading, setUploading] = useState(false);

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    setHasUploaded(false);
    const { files: f } = e.target;
    if (f) setFiles([...files, ...Array.from(f)]);
  };

  const handleFileDrop = (e: any) => {
    if (allowDrop) {
      e.preventDefault();
      const f: FileList = e.dataTransfer.files;
      if (f) setFiles([...files, ...Array.from(f)]);
    }
  };

  const handleDragOver = (e: any) => {
    if (allowDrop) e.preventDefault();
  };

  const fileUploadRef = useRef<HTMLInputElement | null>(null);

  const getSum = (percentages: Map<string, number>) =>
    Array.from(percentages.values()).reduce((acc, item) => acc + item, 0);

  const handleUpload = async () => {
    const _files = files.filter((file) => !imageData.some((data) => data.name === file.name));
    if (_files?.length) {
      setUploading(true);
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const totalSize = files.reduce((acc, file) => acc + file.size, 0);
      const percentages: Map<string, number> = new Map();

      try {
        if (_files?.length) {
          const urls: ImageDataType[] = [];
          let data = new FormData();

          const getData = _files.map(async (item: any) => {
            data = new FormData();
            data.append('file', item);
            const res = await axios.post('https://file-api.getfoodcourt.com/upload', data, {
              headers: {
                Authorization: `Bearer ${token}`
              },
              onUploadProgress: (progressEvent) => {
                if (progressEvent.total && progressEvent.estimated) {
                  const possiblePercentage = (progressEvent.total / totalSize) * 100;
                  const currentPercentage = Math.round(
                    (progressEvent.loaded / progressEvent.total) * possiblePercentage
                  );
                  percentages.set(item.name, currentPercentage);
                  setTimeLeft(getTimeDuration(progressEvent.estimated));
                  setPercent(getSum(percentages));
                }
              }
            });
            return { name: item.name, url: res.data.url } as ImageDataType;
          });

          try {
            await Promise.all(getData).then(function (results) {
              results.map((item) => {
                urls.push(item);
              });
            });
          } catch {
            toast(
              <ToastMessage type='error' message='Problem encounterd while uploading images' />
            );
          }

          if (urls) {
            setImageData([...imageData, ...urls]);
            setPercent(0);
            setTimeLeft('');
            setHasUploaded(true);
          }
        }
      } catch (error) {
        toast(<ToastMessage type='error' message='Problem encounterd while uploading images' />);
      }
      setUploading?.(false);
    }
  };

  const getThumbnail = useMemo(() => {
    if (files?.[0]) {
      handleUpload();
      return URL.createObjectURL(files?.[0]);
    }
    return '';
  }, [files]);

  useEffect(() => {
    if (uploading) {
      setAllowDrop(false);
    } else {
      setPercent(0);
      setAllowDrop(true);
    }
  }, [uploading]);

  return (
    <div className='max-h-[330px] overflow-auto pt-4 pb-2 px-6 rounded-[6px] border border-[#EDEDEE]  space-y-5'>
      <h4 className='font-medium'>Upload images</h4>
      <div
        className={`relative z-[1] border border-dashed border-[#D9D9D9] h-[100px] flex flex-wrap items-center justify-center gap-1 w-full rounded-md text-xs ${
          uploading && 'cursor-not-allowed '
        }`}
        onDrop={handleFileDrop}
        onDragOver={handleDragOver}>
        <UploadIcon className='mr-3' />
        <p className='text-gray-4'> Drop files here or </p>
        <button
          disabled={uploading}
          className={`${uploading ? 'text-gray-4 opacity-40' : 'font-medium text-red-5'}`}
          onClick={() => fileUploadRef?.current?.click()}>
          browse
        </button>
        <input
          multiple
          type='file'
          accept='image/*'
          ref={fileUploadRef}
          onChange={onFileChange}
          disabled={disabled || uploading}
          className='absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer'
        />
      </div>
      {files?.[0] ? (
        <div className='bg-[#F5F4F4] rounded px-8 py-3 space-y-5'>
          <div className='flex items-center gap-6 flex-wrap'>
            <div className='w-[50px] h-[50px] rounded-full bg-white p-2'>
              <div
                className='h-[100%] w-[100%] rounded-[100%]'
                style={{
                  backgroundImage: `url(${getThumbnail})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              />
            </div>

            <div className='grow space-y-2'>
              <label className='flex items-center gap-1 text-sm font-medium text-[#4B4D4E]'>
                {files?.[0].name}
                {files?.length > 1 ? (
                  <span className='flex items-center justify-center p-1 bg-red-5 rounded-lg text-white text-xs font-bold leading-4'>
                    +{files.length - 1}
                  </span>
                ) : null}
              </label>
              <div className='text-xs flex text-gray-5 items-center gap-[7px]'>
                <p>
                  {getFileSize(
                    Array?.from(files)?.reduce(
                      (size: number, file: File) => size + file?.size ?? 0,
                      0
                    )
                  )}
                </p>
                <div className='w-1 h-1 bg-gray-5 rounded-full' />
                {uploading && <p>{timeLeft} left</p>}
              </div>
            </div>
            {hasUploaded && (
              <div
                className='flex flex-col self-end text-xs items-center gap-3 cursor-pointer'
                onClick={() => {
                  setFiles([]);
                  setImageData([]);
                  if (fileUploadRef?.current?.files) fileUploadRef.current.files = null;
                  if (fileUploadRef?.current?.value) fileUploadRef.current.value = '';
                }}>
                <p className='text-red-5 font-semibold'>
                  Remove file{files?.length > 1 ? 's' : ''}
                </p>
                <span className='text-gray-5'>Upload complete!</span>
              </div>
            )}
            {uploading && <span className='self-end text-xs text-gray-5'>{percent}%</span>}
          </div>
          {uploading && (
            <div className='relative overflow-hidden h-[6px] rounded-md w-full bg-white'>
              <div
                style={{
                  height: '100%',
                  width: `${percent}%`,
                  transition: 'width .8s'
                }}
                className={`bg-red-5 rounded-full`}
              />
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default XlsMealsImageUploader;
